import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, NgZone, OnDestroy, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { environment } from '@environments/environments';
import { CreditCardDrawerBaseComponent } from '@shared/components/page-layout/drawers/credit-card-drawer-base.component';
import { buildDropInThemeOverrides } from '@shared/components/page-layout/drawers/jpmorgan-drop-in-theme';
import { DropInUIEvent, DropInUIInstance, DropInUIModule } from '@shared/components/page-layout/drawers/jpmorgan-drop-in-ui.types';
import { BillingErrorResponse } from '@shared/models/response/billing-error-response.model';

const DROP_IN_MOUNT_ELEMENT_ID = 'jpmorgan-dropin-mount';

@Component({
    selector: 'app-jpmorgan-credit-card-drawer',
    templateUrl: './jpmorgan-credit-card-drawer.component.html',
    styleUrls: ['./jpmorgan-credit-card-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIcon, FormsModule, ReactiveFormsModule, MatCheckbox, MatFormField, MatLabel, MatSelect, MatOption, MatInput, MatButton]
})
export class JpMorganCreditCardDrawerComponent extends CreditCardDrawerBaseComponent implements OnDestroy {
    private ngZone = inject(NgZone);

    private dropIn: DropInUIInstance;
    private unsubscribeDropIn: () => void;

    protected setupGatewayFormControls(isEditMode: boolean): void {
        this.creditCardValidationForm.addControl('dropInReady', this.formBuilder.control(isEditMode, isEditMode ? [] : [Validators.requiredTrue]));
    }

    /**
     * Generated before the client-token request and sent as ?reference=; the backend correlates
     * its async payment notification back to this value, and we echo it as externalCardIdNonce
     * when saving the card - CAT-verified contract, not a guess.
     */
    protected override generateTokenReference(): string {
        return crypto.randomUUID();
    }

    protected initializeGateway(token: string): void {
        if (this.userCreditCard?.id) {
            // Editing is metadata-only (nickname/sidemark) - no need to mount a live payment iframe.
            this.busy = false;
            this.changeDetectorRef.detectChanges();
            return;
        }

        // The widget's "Save for future payments (optional)" checkbox (and which fields render
        // at all) is driven by data baked into the checkout session behind this JWT, not by any
        // DropInUIOptions exposed here - there's no client-side flag for it. This drawer only
        // ever vaults a card for later use, so the backend's checkout-session/intent creation
        // needs its own "vault this card" mode so the widget doesn't offer it as optional.
        // Backend follow-up, not resolved in this file.
        this.ngZone.runOutsideAngular(async () => {
            try {
                const dropInModule = await import(/* @vite-ignore */ environment.jpMorganDropInUiUrl) as DropInUIModule;
                const DropInUI = dropInModule.default;

                this.dropIn = new DropInUI({
                    checkoutSessionToken: token,
                    controlledSubmit: true,
                    themeValueOverrides: buildDropInThemeOverrides(DROP_IN_MOUNT_ELEMENT_ID)
                });

                this.unsubscribeDropIn = this.dropIn.subscribe(event => this.onDropInEvent(event));
                this.dropIn.mount(DROP_IN_MOUNT_ELEMENT_ID);
            } catch {
                this.ngZone.run(() => {
                    this.busy = false;
                    this.alertService.errorToast('Error', 'Error initializing payment form. Please try again later.');
                    this.changeDetectorRef.detectChanges();
                });
            }
        });
    }

    protected submitNewCard(): void {
        this.dropIn?.submit();
    }

    ngOnDestroy(): void {
        this.unsubscribeDropIn?.();
        this.dropIn?.clearAllSubscribers();
        try {
            this.dropIn?.unmount();
        } catch {
            // Nothing mounted (e.g. edit mode skipped gateway init) - safe to ignore.
        }
    }

    /**
     * jpm_profile_consent_declined / jpm_saved_method_not_found (backend contract, confirmed
     * 2026-09-20) mean nothing was vaulted, so the consumed checkoutSessionToken/reference can't
     * be reused - tear down the widget and mount a fresh one against a newly generated reference.
     */
    protected override handleSaveCardError(error: HttpErrorResponse): void {
        const code = (error?.error as BillingErrorResponse)?.code;
        switch (code) {
            case 'jpm_profile_consent_declined':
                this.alertService.errorToast('Card Not Saved', 'Your card wasn\'t saved. Please check "Save for future payments" in the payment form and try again.');
                this.remountDropIn();
                break;
            case 'jpm_saved_method_not_found':
                this.alertService.errorToast('Card Not Saved', 'The payment form wasn\'t completed. Please try again.');
                this.remountDropIn();
                break;
            default:
                super.handleSaveCardError(error);
                break;
        }
    }

    private remountDropIn(): void {
        this.unsubscribeDropIn?.();
        this.dropIn?.clearAllSubscribers();
        try {
            this.dropIn?.unmount();
        } catch {
            // Nothing mounted - safe to ignore.
        }
        this.dropIn = undefined;
        this.creditCardValidationForm.controls['dropInReady'].setValue(false);
        this.busy = true;
        this.changeDetectorRef.detectChanges();
        this.requestClientToken();
    }

    private onDropInEvent(event: DropInUIEvent): void {
        switch (event.message) {
            // "FormLoadedAtLeastOnePaymentMethod" is the bundle's actual "Ready" enum value
            // (confirmed via decompiled drop-in-ui.mjs, 2026-09-20) - MountSuccess/InitialLoadComplete
            // already cover this same moment, so this case is belt-and-suspenders.
            case 'FormLoadedAtLeastOnePaymentMethod':
            case 'InitialLoadComplete':
            case 'MountSuccess':
                this.ngZone.run(() => {
                    this.busy = false;
                    this.creditCardValidationForm.controls['dropInReady'].markAsDirty();
                    this.creditCardValidationForm.controls['dropInReady'].setValue(true);
                    this.changeDetectorRef.detectChanges();
                });
                break;
            case 'PaymentSuccess': {
                // externalCardIdNonce is the reference we generated and sent as ?reference= to the
                // client-token call (CAT-verified contract) - NOT parsed from this event's payload.
                // last4/cardType display fields are still a best-effort read of the payload since
                // that shape is unconfirmed against JPMorgan's docs/sandbox.
                const payload = event.payload ?? {};
                this.ngZone.run(() => {
                    this.persistNewCard({
                        externalCardIdNonce: this.tokenReference,
                        last4DigitsCC: payload.lastFour ?? payload.last4,
                        cardType: payload.cardType ?? payload.brand
                    });
                });
                break;
            }
            case 'PaymentError':
            case 'PaymentUnsuccessful':
            case 'PaymentValidationFailed':
                this.ngZone.run(() => {
                    this.busy = false;
                    this.alertService.errorToast('Error', 'Error validating credit card details. Please check your information and try again.');
                    this.changeDetectorRef.detectChanges();
                });
                break;
            default:
                break;
        }
    }
}
